import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  assignCoreApplicationDevice,
  coreAppDeviceServicesQuery,
  coreApplicationDevicesQuery,
  coreApplicationSettingsQuery,
  removeCoreApplicationDevice,
  saveCoreApplicationIcon,
  saveCoreApplicationSettings,
  type CoreAppDeviceRow,
} from '../api/proactivitee'
import { ApiError } from '../api/client'
import { FormActions, FormRow, HalfField } from '../ui/FormGrid'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { APP_ICONS, DEFAULT_APP_ICON, appIconClass } from './appIcon'

/**
 * Randul desfacut din Core Applications - `core_applications/tabList.php`.
 *
 * ---------------------------------------------------------------------------
 * BANDA DE FILE, CARE LIPSEA CU TOTUL
 * ---------------------------------------------------------------------------
 *
 * Portalul desena aici doua file: Devices si Settings. In v4 se desena numai
 * CONTINUTUL primei, fara banda - deci Settings nu se putea deschide de
 * nicaieri, iar omul care cauta persoana de contact a unei aplicatii nu avea
 * unde sa se duca. Banda e cea din portal, cu aceleasi clase
 * (`ul.action_tabs`, `a.action_button`), ca in `CmdbRowTabs`.
 *
 * ---------------------------------------------------------------------------
 * SI DE-ACEEA IESEAU CASUTELE MASINILOR IN AFARA TABELULUI
 * ---------------------------------------------------------------------------
 *
 * `devices.php` isi incepe continutul cu un `<div class="row">`, care are
 * `margin: 0 -15px`. In portal randul ala pica inauntrul lui
 * `.col-md-12 > .details_content` din `tabList.php`, iar cei 15px de padding ai
 * coloanei il anulau exact. Fara banda, `.row`-ul ajungea de-a dreptul in
 * `td.rowDetails`, care n-are padding: masurat in Edge la 1600px, casutele
 * porneau de la x=-14 si se intindeau pe 1628px, adica 15px in afara chenarului
 * tabelului pe fiecare latura, iar butonul „Add device" impingea documentul la
 * 1615px. Odata cu banda, coloana se intoarce si cu ea padding-ul.
 *
 * ---------------------------------------------------------------------------
 * DREPTURILE
 * ---------------------------------------------------------------------------
 *
 * Fiecare fila e desenata numai daca serverul spune ca omul are dreptul ei
 * (`proactivitee.core_app_devices.read`, `proactivitee.core_app_settings.read`),
 * cum facea `canAccess` in macheta. Gardul adevarat ramane pe capete.
 */

type TabSlug = 'devices' | 'settings'

export function CoreAppRow({ applicationId, devices }: { applicationId: number; devices: Record<string, unknown>[] }) {
  const query = useQuery(coreApplicationDevicesQuery(applicationId))
  const [tab, setTab] = useState<TabSlug>('devices')
  const tabs = (query.data?.tabs ?? []).filter((t) => t.allowed)

  /* Daca Devices nu e a lui, se deschide prima fila pe care o are. */
  useEffect(() => {
    if (tabs.length === 0) return
    if (!tabs.some((t) => t.slug === tab)) setTab(tabs[0].slug as TabSlug)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data])

  if (query.isPending) {
    return (
      <div className="row d-flex m-0">
        <div className="col-md-12"><Spinner /></div>
      </div>
    )
  }

  if (tabs.length === 0) {
    return (
      <div className="row d-flex m-0">
        <div className="col-md-12">
          <div className="alert alert-info">
            <i className="fas fa-info-circle"></i> Your role cannot open anything under a core application.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="row d-flex m-0" data-application={applicationId}>
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.map((entry) => (
            <li key={entry.slug}>
              <a
                href={`#${entry.slug}`}
                className={tab === entry.slug ? 'action_button active' : 'action_button'}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(entry.slug as TabSlug)
                }}
              >
                {entry.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'settings' ? (
            <SettingsTab applicationId={applicationId} />
          ) : (
            <DevicesTab applicationId={applicationId} devices={devices} />
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Fila Devices - `core_applications/devices.php`. Se desena o insiruire de
 * randuri fara nume - serverul intorcea numai id-uri - deci omul vedea cifre,
 * nu masini, si nu putea nici lega, nici dezlega o masina de aplicatie.
 */
function DevicesTab({ applicationId, devices }: { applicationId: number; devices: Record<string, unknown>[] }) {
  const query = useQuery(coreApplicationDevicesQuery(applicationId))
  const [assignOpen, setAssignOpen] = useState(false)
  const rows: CoreAppDeviceRow[] = query.data?.data ?? []
  const canWrite = Boolean(query.data?.canWrite)

  async function remove(row: CoreAppDeviceRow) {
    if (!window.confirm('This applications will not be monitored anymore!')) return
    await removeCoreApplicationDevice(applicationId, row.device_id, row.device_service_id)
    void query.refetch()
  }

  return (
    <div className="row">
      <div className="col-md-12">
        {canWrite ? (
          <button
            className="btn btn-primary btn-sm pull-right task-form"
            data-application={applicationId}
            style={{ marginLeft: 10 }}
            onClick={() => setAssignOpen(true)}
          >
            <i className="fas fa-plus" /> Add device
          </button>
        ) : null}
      </div>

      {query.isPending ? (
        <div className="col-md-12"><i className="fas fa-circle-notch fa-spin"></i> Loading...</div>
      ) : rows.length === 0 ? (
        <div className="col-md-12">
          <div className="alert alert-info"><i className="fas fa-info-circle"></i> No devices for this application.</div>
        </div>
      ) : (
        rows.map((row) => (
          <div
            key={`${row.device_id}:${row.device_service_id ?? 'host'}`}
            className="col-md-3 device-box"
            data-toggle="tooltip"
            data-placement="top"
            title={row.service_value ?? ''}
          >
            <div className="licensed-device bg-darkblue color-white">
              <div className="name">
                <i className="fas fa-server" /> {row.device_name}
              </div>
              {/* Fara serviciu ales, legatura urmareste toata masina. */}
              <div className="user">{row.service_name ?? 'HOST (all services)'}</div>
              {canWrite ? (
                <div className="actions remove_application_device">
                  <button type="button" className="btn btn-xs bg-transparent" onClick={() => void remove(row)}>
                    <i className="fas fa-times" />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ))
      )}

      {assignOpen ? (
        <AssignDeviceModal
          applicationId={applicationId}
          devices={devices}
          onClose={() => setAssignOpen(false)}
          onSaved={() => { setAssignOpen(false); void query.refetch() }}
        />
      ) : null}
    </div>
  )
}

/**
 * Alesul de iconite - `#iconPick` din `core_applications/settings.php`.
 *
 * In portal era `bootstrap-iconpicker`, un buton care deschidea tot setul Font
 * Awesome 4.7 intr-un meniu de 6 pe 9, cu pagini. Aici nu ruleaza jQuery, deci
 * butonul ala ar fi ramas un dreptunghi mort, exact ca select-ul de deasupra
 * tabelului; se deseneaza in loc o grila cu o casuta de cautare deasupra.
 *
 * CE SE OFERA: ce oferea si el. Lista si fata sunt in `appIcon.ts` - acolo sta
 * scris de ce sunt 519 din 785, si de ce `fa` si nu `far`. Prima incercare de
 * aici oferea 30 de iconite, pornind de la o premisa gresita: ca fata cu care
 * deseneaza cartonasul e cea OUTLINE. Nu era - fata aia era chiar stricaciunea,
 * si de indata ce a fost reparata, restrictia n-a mai avut nimic in spate.
 *
 * Butoanele se deseneaza cu ACEEASI clasa cu care se deseneaza cartonasul,
 * ceruta din acelasi loc: altfel omul alege dintr-o fata si primeste alta,
 * fix greseala care se repara aici.
 */

/**
 * Fila Settings - `core_applications/settings.php` si cele doua scrieri ale ei
 * (`saveCoreApplicationSettings`, `saveCoreApplicationIcon`).
 *
 * Campurile stau pe grila comuna (`FormGrid`), nu pe `col-sm-2` / `col-sm-4`
 * scrise de mana in macheta veche: panoul se deschide INAUNTRUL unui rand de
 * tabel, iar acolo `.form-inline` al invelisului de lista desface orice grila
 * care nu poarta `od-form-row`. Sta scris pe larg in capul lui `FormGrid.tsx`.
 */
function SettingsTab({ applicationId }: { applicationId: number }) {
  const query = useQuery(coreApplicationSettingsQuery(applicationId))
  const [fields, setFields] = useState({ technology_name: '', contact_name: '', contact_email: '', contact_phone: '' })
  const [icon, setIcon] = useState(DEFAULT_APP_ICON)
  const [iconSearch, setIconSearch] = useState('')
  const [busy, setBusy] = useState(false)

  /* Cautarea e „contine", fara litera mare, pe numele iconitei - acelasi fel in
     care cauta si casuta alesului din portal. */
  const shownIcons = useMemo(() => {
    const needle = iconSearch.trim().toLowerCase()
    if (needle === '') return APP_ICONS
    return APP_ICONS.filter((name) => name.slice(3).includes(needle))
  }, [iconSearch])

  useEffect(() => {
    const data = query.data
    if (!data) return
    setFields({
      technology_name: data.technology_name ?? '',
      contact_name: data.contact?.contact_name ?? '',
      contact_email: data.contact?.contact_email ?? '',
      contact_phone: data.contact?.contact_phone ?? '',
    })
    setIcon(data.icon || DEFAULT_APP_ICON)
  }, [query.data])

  if (query.isPending) return <Spinner />
  if (query.error instanceof ApiError && query.error.status === 403) {
    return <div className="alert alert-info"><i className="fas fa-info-circle"></i> Your role cannot open the settings of a core application.</div>
  }
  if (query.error) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle"></i> {query.error.message}
      </div>
    )
  }

  const canWrite = Boolean(query.data?.canWrite)
  const set = (key: keyof typeof fields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFields((prev) => ({ ...prev, [key]: e.target.value }))

  async function saveSettings() {
    setBusy(true)
    try {
      const result = await saveCoreApplicationSettings(applicationId, fields)
      toast(result.message, 'Core Applications', result.status === 'OK' ? 'success' : 'error')
      if (result.status === 'OK') void query.refetch()
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Settings could not be saved.', 'Core Applications', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function saveIcon(next: string) {
    setIcon(next)
    try {
      const result = await saveCoreApplicationIcon(applicationId, next)
      toast(result.message, 'Core Applications', result.status === 'OK' ? 'success' : 'error')
      if (result.status === 'OK') void query.refetch()
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Icon could not be saved.', 'Core Applications', 'error')
    }
  }

  return (
    <div className="od-core-app-settings">
      <FormRow>
        <HalfField label="Technology name" htmlFor={`ca-tech-${applicationId}`}>
          <input
            id={`ca-tech-${applicationId}`}
            type="text"
            className="form-control"
            value={fields.technology_name}
            disabled={!canWrite}
            onChange={set('technology_name')}
          />
        </HalfField>
        <HalfField
          label="Contact name"
          required
          htmlFor={`ca-contact-${applicationId}`}
          help="Who gets called when this application is down. Applications Dashboard shows it in the drawer."
        >
          <input
            id={`ca-contact-${applicationId}`}
            type="text"
            className="form-control"
            value={fields.contact_name}
            disabled={!canWrite}
            onChange={set('contact_name')}
          />
        </HalfField>
      </FormRow>
      <FormRow>
        <HalfField label="Contact email" htmlFor={`ca-email-${applicationId}`}>
          <input
            id={`ca-email-${applicationId}`}
            type="email"
            className="form-control"
            value={fields.contact_email}
            disabled={!canWrite}
            onChange={set('contact_email')}
          />
        </HalfField>
        <HalfField label="Contact phone" htmlFor={`ca-phone-${applicationId}`}>
          <input
            id={`ca-phone-${applicationId}`}
            type="text"
            className="form-control"
            value={fields.contact_phone}
            disabled={!canWrite}
            onChange={set('contact_phone')}
          />
        </HalfField>
      </FormRow>
      {canWrite ? (
        <FormActions>
          <button type="button" className="btn btn-primary applicationSettings" disabled={busy} onClick={() => void saveSettings()}>
            {busy ? <i className="fas fa-circle-notch fa-spin"></i> : null} Save settings
          </button>
        </FormActions>
      ) : null}

      <hr className="dotted" />

      <FormRow>
        <HalfField
          label="Icon"
          span={12}
          help={
            APP_ICONS.includes(icon)
              ? 'Shown on the card in Core Applications Dashboard.'
              : /*
                 * Nu e aceeasi poveste ca inainte. Toate cele 519 de mai jos se
                 * deseneaza - si `fa-database`, si marcile. Ce NU se deseneaza
                 * sunt cele 266 de nume pe care Font Awesome 5 le-a scos sau
                 * redenumit fata de 4 (94 dintre ele sunt variantele `-o`, pe
                 * care alesul vechi chiar le oferea). O aplicatie care poarta
                 * unul din alea l-a primit in portal si nu-l mai poate desena
                 * nimeni: nu are clasa in nicio foaie pe care o incarcam.
                 */
                `Shown on the card in Core Applications Dashboard. This application carries "${icon}", a Font Awesome 4 name that version 5 dropped - nothing can draw it until one below is picked.`
          }
        >
          <div>
            {/* Casuta de cautare, ca in meniul alesului din portal: 519 de
                iconite nu se aleg cu ochiul, se aleg dupa nume. */}
            <input
              type="search"
              id={`ca-icon-search-${applicationId}`}
              className="form-control input-sm"
              placeholder="Search icons..."
              aria-controls={`ca-icons-${applicationId}`}
              value={iconSearch}
              disabled={!canWrite}
              onChange={(e) => setIconSearch(e.target.value)}
              style={{ maxWidth: 260, marginBottom: 8 }}
            />
            <div
              id={`ca-icons-${applicationId}`}
              style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 180, overflowY: 'auto' }}
            >
              {shownIcons.length === 0 ? (
                <span className="help-block" style={{ margin: 0 }}>No icon matches &quot;{iconSearch.trim()}&quot;.</span>
              ) : (
                shownIcons.map((name) => (
                  <button
                    key={name}
                    type="button"
                    title={name.replace('fa-', '').replace(/-/g, ' ')}
                    aria-pressed={icon === name}
                    className={icon === name ? 'btn btn-primary od-icon-pick' : 'btn btn-default od-icon-pick'}
                    disabled={!canWrite}
                    style={{ width: 38, height: 34 }}
                    onClick={() => void saveIcon(name)}
                    /* Grila curge; cea aleasa poate fi la randul 40. Se aduce in
                       dreptul ochiului la deschidere, ca omul sa vada ce e pus
                       fara sa caute. */
                    ref={(el) => {
                      if (!el || icon !== name || iconSearch !== '') return
                      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' })
                    }}
                  >
                    <i className={appIconClass(name)} aria-hidden="true"></i>
                  </button>
                ))
              )}
            </div>
          </div>
        </HalfField>
      </FormRow>
    </div>
  )
}

/** #assignDeviceModal: masina, apoi serviciile ei urmarite (goale = toata masina). */
function AssignDeviceModal({ applicationId, devices, onClose, onSaved }: {
  applicationId: number
  devices: Record<string, unknown>[]
  onClose: () => void
  onSaved: () => void
}) {
  const [device, setDevice] = useState(0)
  const [services, setServices] = useState<number[]>([])
  const [error, setError] = useState('')
  const list = useQuery({ ...coreAppDeviceServicesQuery(device), enabled: device > 0 })

  async function save() {
    if (device < 1) {
      setError('Pick a device first.')
      return
    }
    const result = await assignCoreApplicationDevice(applicationId, device, services)
    if (result.status === 'OK') {
      onSaved()
      return
    }
    setError(result.message)
  }

  return (
    <div className="modal fade in" id="assignDeviceModal" tabIndex={-1} role="dialog" style={{ display: 'block' }}>
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <button type="button" className="close" aria-label="Close" onClick={onClose}><span aria-hidden="true">&times;</span></button>
            <h4 className="modal-title">Assign device</h4>
          </div>
          <div className="modal-body">
            {error ? <div className="alert alert-danger">{error}</div> : null}
            <div className="form-group">
              <label htmlFor="ad-device">Device</label>
              <select
                id="ad-device"
                className="form-control"
                value={device || ''}
                onChange={(e) => { setDevice(Number(e.target.value)); setServices([]) }}
              >
                <option value="">Select..</option>
                {devices.map((d) => (
                  <option key={String(d.ID)} value={String(d.ID)}>{String(d.Denumire)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="ad-services">Services <small className="text-muted">(none = the whole machine)</small></label>
              <select
                id="ad-services"
                className="form-control"
                multiple
                value={services.map(String)}
                onChange={(e) => setServices(Array.from(e.target.selectedOptions).map((o) => Number(o.value)))}
              >
                {(list.data?.services ?? []).map((svc) => (
                  <option key={svc.device_service_id} value={svc.device_service_id} title={svc.service_value ?? ''}>
                    {svc.service_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-default" onClick={onClose}>Close</button>
            <button type="button" className="btn btn-primary" onClick={() => void save()}>Save</button>
          </div>
        </div>
      </div>
    </div>
  )
}
